(ns hai.tasks.facets
  "The kanban tasks-board filter surfaces: the tag + assignee facet sidebar - the
   one place the board's filters are set - and the applied-filter chips shown in
   the page header. Both drive the collection's standard `:table/filters` form (the
   sub-field `set-value!` for the sidebar, the map-level field for the chips), so
   the query plumbing is untouched.

   Options and their counts have two sources, chosen by the
   `:feature-flag/unified-relationships?` flag: OFF reads the standalone
   `:user/task-facets` resolver via [[hai.tasks.data/load-facets]]; ON reads the
   board collection's `:collection.filters/options` straight off its own query
   response (no separate round-trip), adapted to the OFF shape by `options->facets`.

   A task-specific fork of `hai.notes.facets`, narrowed to the board's two
   dimensions (tag and assignee)."
  (:require
   [hai.tasks.data :as data]
   [hai.ui.components.collection.facets :as collection-facets]
   [hai.ui.components.collection.filters :as filters]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private person-icon (icons/mui-icon :outlined :person))

;; The filter attr the assignee facet writes. A `:user/uuid` selects that
;; assignee; a no-value sentinel selects tasks with no assignee.
(def ^:private assigned-to-attr :task/assigned-to)

;; The ON composable-filter path's universal no-value selector (matches the BE
;; `:collection.filter/no-value`). Under ON the sidebar writes it for the
;; Untagged / Unassigned rows; OFF keeps the legacy `:untagged` / `:unassigned`
;; sentinels its facet path understands.
(def ^:private no-value-sentinel :collection.filter/no-value)

;; ---------------------------------------------------------------------------
;; Applied-filter chips (page header)
;; ---------------------------------------------------------------------------

(o/defstyled chips-row :div
  {:display "flex" :align-items "center" :flex-wrap "wrap"
   :gap "6px" :min-width "0"})

(o/defstyled filter-chip :span
  {:display "inline-flex" :align-items "center" :gap "6px"
   :height "26px" :padding "0 4px 0 10px"
   :border-radius "999px"
   :border-width "1px" :border-style "solid" :border-color t/--card-forest-14
   :background t/--colour-snow
   :font-size "12px" :color t/--card-forest
   :white-space "nowrap"}
  [:b {:font-weight "600"}]
  [:.lead-icon {:display "inline-flex"}
   [:svg {:width "13px" :height "13px" :fill "currentColor"}]]
  [:.remove
   {:display "inline-flex" :align-items "center" :justify-content "center"
    :width "18px" :height "18px" :border "none" :border-radius "999px"
    :background "transparent" :padding "0"
    :color t/--card-forest :cursor "pointer"}
   [:&:hover {:background t/--card-forest-10}]
   [:svg {:width "11px" :height "11px" :fill "currentColor"}]])

(defn applied-chips
  "The header's row of currently-applied filters - one chip per filter column
   holding a value, showing the column's pill face (`:label`/`:icon`/`:summary`)
   with an × that resets it. Clearing sets the field on the filters SUB-form
   (id `[<root-form-id> :table/filters]`) - the same path the sidebar writes -
   because sub-form values propagate up to the root form but a root-level write
   never reaches the sub-form's fields, which would leave the sidebar's
   selection highlight stale. Renders nothing when no filter is applied."
  [table]
  (let [unified? @(<s [:feature-flag/unified-relationships?])
        columns (<s [::filters/columns table])
        filters-form-id [(-> table :table/form :form/id) :table/filters]
        {:form.field/keys [current-value]}
        (get-in table [:table/form :form/fields :table/filters])
        applied (for [{:table.column/keys [attr filter] :as column} @columns
                      :let [{:table.column.filter/keys [label icon summary]} filter
                            value (get current-value attr)]
                      :when (and label (some? value))]
                  ^{:key attr}
                  [filter-chip
                   (when icon [:span.lead-icon [icon]])
                   [:b (str label ":")]
                   [:span (when summary (summary value column))]
                   [:button.remove
                    {:type "button"
                     :aria-label (str "Reset " label " filter")
                     :on-click (fn []
                                 (>e [:form.field/set-value! filters-form-id attr nil])
                                 (when-not unified? (>e [::data/load-facets])))}
                    [icons/outlined-close]]])]
    (when (seq applied)
      (into [chips-row] applied))))

;; ---------------------------------------------------------------------------
;; Facet sidebar
;; ---------------------------------------------------------------------------

;; The shared sidebar, in the tasks palette.
(o/defstyled sidebar collection-facets/sidebar
  {:--facet-border    t/--card-forest-14
   :--facet-muted     t/--card-forest-70
   :--facet-ink       t/--card-forest
   :--facet-tint      t/--card-forest-10
   :--facet-scrollbar "rgba(45,62,42,0.4)"})

(defn- facet-option
  [{:keys [label amount selected? on-toggle title]}]
  [:button.facet-option
   {:type     "button"
    :class    (cond-> []
                selected? (conj "selected")
                ;; Nothing matches this option under the current constraints.
                (and (some? amount) (zero? amount) (not selected?)) (conj "zero"))
    :title    (or title label)
    :on-click on-toggle}
   [:span.opt-label label]
   (when (some? amount) [:span.opt-count amount])])

(defn- toggle-value!
  "Single-select facet click: picking the selected option again clears it.
   Under OFF the counts re-query via ::data/load-facets - the set event is
   processed first, so the reload sees the new value. Under ON the counts ride
   the board collection's re-resolve that the filter-field change triggers, so no
   separate facets fetch is needed."
  [unified? set-value! current-value value]
  (set-value! (if (= current-value value) nil value))
  (when-not unified? (>e [::data/load-facets])))

(defn- field-state [table attr]
  (get-in table [:table/form :form/fields attr]))

(defn- clear-all!
  [unified? table]
  (doseq [[_ {:form.field/keys [set-value! current-value]}]
          (-> table :table/form :form/fields)]
    (when (and set-value! (some? current-value))
      (set-value! nil)))
  (when-not unified? (>e [::data/load-facets])))

(defn- tags-group
  "The tag facet group: an Untagged sentinel row over every tag on the board's
   open tasks, each with its smart count. Single-select against the `:task/tags`
   filter field. The value for a tag is a one-element name vector `[tag-name]`
   (the name-based filter the server reuses from notes; the engine accepts a
   vector of values too); the sentinel is `:untagged` under OFF, the universal
   no-value sentinel under ON. Mirrors the notes wall's `tags-group`."
  [unified? table tags untagged-count]
  (let [{:form.field/keys [current-value set-value!]} (field-state table :task/tags)
        untagged-value (if unified? no-value-sentinel :untagged)]
    [collection-facets/facet-group {:icon icons/humble-tag :heading "Tag"}
     (into [:div.facet-options
            [facet-option {:label     "Untagged"
                           :amount    untagged-count
                           :selected? (= current-value untagged-value)
                           :on-toggle #(toggle-value! unified? set-value! current-value untagged-value)}]]
           (for [{tag-name :tag/name amount :facet/count} tags
                 :let [value [tag-name]]]
             ^{:key tag-name}
             [facet-option {:label     (str "#" tag-name)
                            :amount    amount
                            :selected? (= current-value value)
                            :on-toggle #(toggle-value! unified? set-value! current-value value)}]))]))

(defn- assignees-group
  "The single assignee facet group: an Unassigned sentinel row over every
   distinct assignee in the board's open tasks, each with its smart count.
   Single-select against the `:task/assigned-to` filter field."
  [unified? table assignees unassigned-count]
  (let [{:form.field/keys [current-value set-value!]} (field-state table assigned-to-attr)
        unassigned-value (if unified? no-value-sentinel :unassigned)]
    [collection-facets/facet-group {:icon person-icon :heading "Assignee"}
     (into [:div.facet-options
            [facet-option {:label     "Unassigned"
                           :amount    unassigned-count
                           :selected? (= current-value unassigned-value)
                           :on-toggle #(toggle-value! unified? set-value! current-value unassigned-value)}]]
           (for [{uuid :user/uuid full-name :user/full-name amount :facet/count} assignees]
             ^{:key uuid}
             [facet-option {:label     full-name
                            :amount    amount
                            :selected? (= current-value uuid)
                            :on-toggle #(toggle-value! unified? set-value! current-value uuid)}]))]))

(defn- sentinel-count
  "The count on a dimension's no-value option, or 0 when it is absent."
  [options]
  (or (some (fn [{:keys [value count]}]
              (when (= value no-value-sentinel) count))
            options)
      0))

(defn- options->facets
  "Adapt the ON board collection's `:collection.filters/options`
   (`{dim [{:value :label :count}]}`, sentinel-first) into the same
   `{:facet/tags :facet/untagged-count :facet/assignees :facet/unassigned-count}`
   shape the OFF `::data/facets` sub returns, so `tags-group` / `assignees-group`
   render identically either way. A tag option's `:label` is the tag name; an
   assignee option's `:value` is the `:user/uuid` and `:label` the full name; the
   sentinel option carries the untagged / unassigned count. nil in, nil out
   (the collection has not resolved yet)."
  [options]
  (when options
    (let [tag-opts      (get options :task/tags)
          assignee-opts (get options assigned-to-attr)]
      {:facet/untagged-count   (sentinel-count tag-opts)
       :facet/tags             (for [{:keys [value label count]} tag-opts
                                     :when (not= value no-value-sentinel)]
                                 {:tag/name label :facet/count count})
       :facet/unassigned-count (sentinel-count assignee-opts)
       :facet/assignees        (for [{:keys [value label count]} assignee-opts
                                     :when (not= value no-value-sentinel)]
                                 {:user/uuid value :user/full-name label :facet/count count})})))

(defn- skeleton-group
  "A section heading over a single pulsing placeholder bar, shown per dimension
   while the first facet load is pending."
  [icon heading width]
  [collection-facets/facet-group {:icon icon :heading heading}
   [:div.facet-options
    [:div.facet-skeleton {:style {:width width}}]]])

(defn- facet-groups
  "The sidebar body over `facets` (the `{:facet/tags :facet/untagged-count
   :facet/assignees :facet/unassigned-count}` shape, from either source): the
   skeleton while `facets` is nil (the first load pending), else the tag +
   assignee groups plus the clear-all control. `unified?` decides whether a
   toggle re-queries the standalone facets (OFF) or lets the counts ride the
   collection re-resolve (ON)."
  [unified? table facets]
  (let [{:facet/keys [tags untagged-count assignees unassigned-count]} facets
        any-applied? (some (fn [[_ {:form.field/keys [current-value]}]]
                             (some? current-value))
                           (-> table :table/form :form/fields))]
    (if (nil? facets)
      [sidebar
       [skeleton-group icons/humble-tag "Tag" "78%"]
       [skeleton-group person-icon "Assignee" "72%"]]
      [sidebar
       [tags-group unified? table tags untagged-count]
       [assignees-group unified? table assignees unassigned-count]
       (when any-applied?
         [:button.clear-all
          {:type "button" :on-click #(clear-all! unified? table)}
          "Clear all filters"])])))

(defn- sidebar-panel [table]
  (if @(<s [:feature-flag/unified-relationships?])
    ;; ON: tag + assignee options + counts ride the board collection's response
    ;; (`:collection.filters/options`). The collection auto-resolves on mount and
    ;; re-resolves on every filter change, so there is no separate facets
    ;; round-trip to prime or refresh.
    [facet-groups true table
     (options->facets @(<s [:collection.filters/options data/board-collection-id]))]
    ;; OFF: prime the standalone :user/task-facets counts on mount, then render
    ;; from the ::data/facets sub (nil until the first load lands; a
    ;; loaded-but-empty board comes back as a non-nil map).
    (r/with-let [_ (>e [::data/load-facets])]
      [facet-groups false table @(<s [::data/facets])])))

(defn panel
  "`:filters/render` for the tasks board: the tag + assignee facet sidebar. The
   kanban view mounts it as a sibling of the lane grid (via `:filters/placement
   :sibling`); the page grid places it in the left column."
  [table]
  [sidebar-panel table])
